import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Shared "native bottom sheet" chrome used on phones by:
 *   - ui/dialog        (every modal of the app becomes a sheet)
 *   - ui/dropdown-menu (menus become action sheets)
 *   - ActionSheet / ConfirmSheet
 *
 * Built on vaul (Radix Dialog under the hood) which brings the iOS-like spring,
 * velocity-based swipe-to-dismiss, scroll-lock and on-screen-keyboard handling.
 */

// Class tokens that a caller passes to a desktop <DialogContent> and that still
// make sense inside a sheet. Everything else (max-w-*, rounded-*, overflow-*,
// sm:*, …) describes the desktop floating box and is dropped.
const INNER_TOKEN =
  /^(?:flex|grid|block|flex-col|flex-row|items-\S+|justify-\S+|content-\S+|gap(?:-[xy])?-\S+|space-[xy]-\S+|p[xytblr]?-\S+)$/;
const OUTER_TOKEN = /^(?:bg-\S+|border(?:-\S+)?)$/;

// Look of a tappable row inside an action sheet (also used by ui/dropdown-menu).
export const SHEET_ROW_CLASS =
  "flex min-h-[52px] w-full items-center gap-0 rounded-xl px-3 py-3 text-left text-[15px] font-medium text-neutral-900 outline-none active:bg-stone-100 disabled:opacity-40 [&>svg]:mr-3 [&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:text-neutral-500";

export function splitSheetClass(className = "") {
  const tokens = String(className).split(/\s+/).filter(Boolean);
  return {
    hideClose: tokens.includes("[&>button]:hidden"),
    flush: tokens.includes("p-0"),
    inner: tokens.filter((t) => INNER_TOKEN.test(t)).join(" "),
    outer: tokens.filter((t) => OUTER_TOKEN.test(t)).join(" "),
  };
}

export const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-black/45", className)}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

export function SheetHandle({ className }) {
  return (
    <div
      aria-hidden
      className={cn("mx-auto mb-1 mt-2.5 h-1.5 w-10 shrink-0 rounded-full bg-stone-300", className)}
    />
  );
}

/**
 * <SheetShell> — portal + overlay + bottom-anchored content with a grab handle,
 * a scrollable body and (optionally) a round close button.
 */
export const SheetShell = React.forwardRef(
  (
    {
      className, // outer (the sheet surface)
      bodyClassName, // inner scrollable body
      flush = false, // no horizontal padding in the body (content brings its own)
      showClose = true,
      closeLabel = "Close",
      children,
      ...props
    },
    ref
  ) => {
    const bodyRef = React.useRef(null);
    return (
      <DrawerPrimitive.Portal>
        <SheetOverlay />
        <DrawerPrimitive.Content
          ref={ref}
          // Don't pull focus into the first field (which would pop the keyboard up
          // over a sheet the user only wanted to read): focus the sheet itself unless a
          // field already took the focus (React's `autoFocus`).
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const el = bodyRef.current;
            if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
          }}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[92dvh] w-full flex-col rounded-t-[20px] bg-background outline-none",
            "shadow-[0_-10px_40px_rgba(0,0,0,0.18)] sm:max-w-xl",
            className
          )}
          {...props}
        >
          <SheetHandle />
          <div
            ref={bodyRef}
            tabIndex={-1}
            style={{
              "--sheet-px": flush ? "0px" : "1.25rem",
              // Bottom padding of the scroller; a sticky footer cancels it so it sits flush with the edge.
              "--sheet-pb": "max(1rem, env(safe-area-inset-bottom, 0px))",
            }}
            className={cn(
              "min-h-0 flex-1 overflow-y-auto overscroll-contain outline-none",
              flush ? "" : "px-5 pt-1",
              "pb-[var(--sheet-pb)]",
              bodyClassName
            )}
          >
            {children}
          </div>
          {showClose && (
            <DrawerPrimitive.Close
              aria-label={closeLabel}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-stone-100 text-neutral-600 active:bg-stone-200 focus:outline-none"
            >
              <X className="h-4 w-4" strokeWidth={2.25} />
              <span className="sr-only">{closeLabel}</span>
            </DrawerPrimitive.Close>
          )}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    );
  }
);
SheetShell.displayName = "SheetShell";

export const SheetTitle = DrawerPrimitive.Title;
export const SheetDescription = DrawerPrimitive.Description;
export const SheetClose = DrawerPrimitive.Close;
export const SheetRoot = DrawerPrimitive.Root;
export const SheetTrigger = DrawerPrimitive.Trigger;
